import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'react-router-dom';
import CapRow from '@capillarytech/cap-ui-library/CapRow';
import CapColumn from '@capillarytech/cap-ui-library/CapColumn';
import CapHeading from '@capillarytech/cap-ui-library/CapHeading';
import CapLabel from '@capillarytech/cap-ui-library/CapLabel';
import CapInput from '@capillarytech/cap-ui-library/CapInput';
import CapButton from '@capillarytech/cap-ui-library/CapButton';
import CapIcon from '@capillarytech/cap-ui-library/CapIcon';
import CapImage from '@capillarytech/cap-ui-library/CapImage';
import CapSkeleton from '@capillarytech/cap-ui-library/CapSkeleton';
import CapImportMFEComponent from '@capillarytech/cap-ui-library/CapImportMFEComponent';
import CapLink from '@capillarytech/cap-ui-library/CapLink';
import capillaryLogo from '../../../assets/capillary-logo.svg';
import withStyles from 'utils/withStyles';
import { getConfigValue } from 'utils/commonUtils';
import { generateLoginMarketingMFEProps } from '../Cap/utils';
import messages from './messages';
import {
  makeSelectLoginV2,
  makeSelectOtpSessionGuard,
} from './selectors';
import {
  loginV2,
  ssoLoginV2,
  checkSsoDomain,
  clearError,
  resetPasswordField,
  checkOtpSession,
  logoutV2,
} from './actions';
import { isEmail } from '../../../utils/validationHelper';
import { OTP_SESSION_GUARD, INVALID_CREDENTIALS_MESSAGE } from './constants';
import styles from './styles';

export const NewLogin = ({
  className,
  intl: { formatMessage },
  actions,
  loginV2: { isLoading, error, showPasswordField, checkingSsoDomain },
  history,
  location = {},
  otpSessionGuard,
}) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const ssoCheckInProgress = React.useRef(false);

  const currentPathname = location?.pathname;

  useEffect(() => {
    const newLoginUrl = getConfigValue('new_login_url');
    
    if (currentPathname === newLoginUrl && showPasswordField) {
      actions.resetPasswordField();
      actions.clearError();
      setFormData(prev => ({
        username: prev.username || '',
        password: '',
      }));
    }
  }, [currentPathname]); // Reset when route changes

  useEffect(() => {
    if (!checkingSsoDomain) {
      ssoCheckInProgress.current = false;
    }
  }, [checkingSsoDomain]);


  const seenFreshProbe = React.useRef(false);

  useEffect(() => {
    actions.checkOtpSession();
  }, []);

  useEffect(() => {
    if (otpSessionGuard === OTP_SESSION_GUARD.PENDING) {
      seenFreshProbe.current = true;
      return;
    }
    if (!seenFreshProbe.current) return;
    if (otpSessionGuard === OTP_SESSION_GUARD.VALID) {
      // OTP-pending cookies still set → clear them via v2 logout
      actions.logoutV2();
    }
  }, [otpSessionGuard]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Note: do NOT clear errorMessage on every keystroke. Toggling CapInput's
    // errorMessage prop flips the affix-wrapper status, which remounts the
    // inner <input> and causes focus loss after the first character.
    // Errors clear in handleContinue when the user resubmits.
  };

  const handleEmailBlur = () => {
    if (showPasswordField) return;
    const value = formData.username;
    if (value && value.trim() && !validateEmail(value)) {
      setEmailError(formatMessage(messages.emailInvalid));
    } else {
      // Empty or valid: clear any prior error so the user can recover
      // after correcting a previously-invalid value.
      setEmailError('');
    }
  };
  
  const validateEmail = (email) => isEmail((email || '').trim());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.repeat) {
      e.preventDefault();
      handleContinue();
    }
  };

  // We render two CapInputs — one type="password", one type="text" — and
  // toggle which is visible. Changing a single input's type causes antd to
  // remount the inner <input>, which lets autoFocus re-fire and resets the
  // caret no matter how we time setSelectionRange. With two siblings the
  // type attribute never changes, so the browser keeps each input's
  // selection forever. We just move focus + caret between them on toggle.
  const passwordCaretRef = useRef({ start: 0, end: 0 });

  const togglePasswordVisibility = () => {
    const activeId = showPassword ? 'login-password-visible' : 'login-password';
    const active = document.getElementById(activeId);
    const len = active?.value?.length ?? 0;
    passwordCaretRef.current = {
      start: active?.selectionStart ?? len,
      end: active?.selectionEnd ?? len,
    };
    setShowPassword(prev => !prev);
  };

  useLayoutEffect(() => {
    const targetId = showPassword ? 'login-password-visible' : 'login-password';
    const target = document.getElementById(targetId);
    if (!target || !target.value) return; // skip on mount / empty field
    target.focus();
    target.setSelectionRange(passwordCaretRef.current.start, passwordCaretRef.current.end);
  }, [showPassword]);

  const handleContinue = () => {
    // Clear previous errors on each submit attempt (deferred from handleInputChange
    // to keep focus stable while typing).
    actions.clearError();

    // If password field is NOT shown yet, validate email and check SSO domain
    if (!showPasswordField) {
      // Validate email format first
      if (!formData.username || !formData.username.trim()) {
        setEmailError(formatMessage(messages.emailRequired));
        return;
      }

      if (!validateEmail(formData.username)) {
        setEmailError(formatMessage(messages.emailInvalid));
        return;
      }

      // Email is valid, proceed with SSO check
      // ssoCheckInProgress ref guards against stale Redux props before React re-renders
      if (!checkingSsoDomain && !ssoCheckInProgress.current) {
        ssoCheckInProgress.current = true;
        setEmailError('');
        actions.checkSsoDomain(formData.username.trim());
      }
    }
    // If password field IS shown, proceed with normal login
    else {
      setEmailError('');
      if (formData.username && formData.password && !isLoading) {
        handleSubmit();
      }
    }
  };

  const handleSubmit = () => {
    actions.loginV2({
      body: {
        ...formData,
        username: formData.username.trim(),
      },
    });
  };

  const handleForgotPassword = () => {
    const forgotPasswordUrl = getConfigValue('forgot_password_url');
    history.push(forgotPasswordUrl);
  };

  const handleBackToEmail = (e) => {
    // Prevent navigation - just reset form state
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    actions.resetPasswordField();
    actions.clearError();
    // Reset to email-only state
    setFormData({
      username: formData.username,  // Keep email
      password: '',                 // Clear password
    });
  };
  
    const isGuardLoading = otpSessionGuard !== OTP_SESSION_GUARD.INVALID;

    
    const passwordErrorMessage = error
      ? (error === INVALID_CREDENTIALS_MESSAGE ? formatMessage(messages.passwordIncorrect) : error)
      : undefined;

    return (
    <CapRow className={className}>
      <CapRow type="flex" className="login-page-container">
        <CapColumn
          xs={0}
          sm={0}
          md={12}
          lg={12}
          xl={12}
          xxl={12}
          className="left-section"
        >
          <CapRow className="login-banner-container">
          <CapImportMFEComponent
            system={generateLoginMarketingMFEProps()}
          />

          </CapRow>
        </CapColumn>
        <CapColumn
          xs={24}
          sm={24}
          md={12}
          lg={12}
          xl={12}
          xxl={12}
          className="right-section"
        >
          {isGuardLoading && (
            <div style={{ padding: 24 }}>
              <CapSkeleton active title paragraph={{ rows: 6 }} />
            </div>
          )}
          <div style={{ display: isGuardLoading ? 'none' : undefined }}>
          <CapRow className="login-content">
            <CapRow className="logo-section">
              <CapImage
                src={capillaryLogo}
                alt="Capillary Logo"
                className="capillary-logo"
              />
            </CapRow>
            
            <CapRow className="header-section">
              <CapHeading 
                className="main-title"
              >
                {showPasswordField 
                  ? formatMessage(messages.welcomeBack)
                  : formatMessage(messages.welcomeToCapillary)
                }
              </CapHeading>
              {!showPasswordField && (
                <CapLabel 
                  type="label1" 
                  className="sign-in-label"
                >
                  {formatMessage(messages.signInToYourAccount)}
                </CapLabel>
              )}
            </CapRow>

            <CapRow className={`login-form ${showPasswordField ? 'password-mode' : ''}`}>
              {/* Email Field - Show as input when password field is hidden, as text when password field is shown */}
              {!showPasswordField ? (
                <CapRow className="form-field">
                  <CapInput
                    id="login-username"
                    type="email"
                    placeholder={formatMessage(messages.emailPlaceholder)}
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    onBlur={handleEmailBlur}
                    onKeyDown={handleKeyDown}
                    size="large"
                    className="username-input"
                    tabIndex={1}
                    errorMessage={(emailError || error) || undefined}
                  />
                </CapRow>
              ) : (
                <CapRow className="email-display-section">
                  <CapLabel 
                    type="label1" 
                    className="email-display-text"
                  >
                    {formData.username}
                  </CapLabel>
                  <CapLink
                    className="change-email-link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBackToEmail(e);
                    }}
                    tabIndex={5}
                  >
                    {formatMessage(messages.changeEmail)}
                  </CapLink>
                </CapRow>
              )}

              {showPasswordField && (
                <CapRow className="form-field password-field-animated">
                  {/* Real type="password" input — always mounted, hidden when "shown". */}
                  <CapInput
                    id="login-password"
                    type="password"
                    placeholder={formatMessage(messages.passwordPlaceholder)}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    onKeyDown={handleKeyDown}
                    size="large"
                    className={`password-input ${showPassword ? 'is-hidden' : ''}`}
                    tabIndex={showPassword ? -1 : 2}
                    autoFocus
                    errorMessage={passwordErrorMessage}
                    suffix={
                      <CapIcon
                        type="eye-invisible"
                        size="m"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={togglePasswordVisibility}
                        className="password-toggle-icon pointer-cursor"
                      />
                    }
                  />
                  {/* type="text" mirror — always mounted, visible only when "shown". */}
                  <CapInput
                    id="login-password-visible"
                    type="text"
                    placeholder={formatMessage(messages.passwordPlaceholder)}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    onKeyDown={handleKeyDown}
                    size="large"
                    className={`password-input ${showPassword ? '' : 'is-hidden'}`}
                    tabIndex={showPassword ? 2 : -1}
                    errorMessage={passwordErrorMessage}
                    suffix={
                      <CapIcon
                        type="eye"
                        size="m"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={togglePasswordVisibility}
                        className="password-toggle-icon pointer-cursor"
                      />
                    }
                  />
                </CapRow>
              )}

              {showPasswordField && (
                <CapRow className="forgot-password-section">
                  <CapLink
                    className="forgot-password-link"
                    onClick={handleForgotPassword}
                    tabIndex={5}
                  >
                    {formatMessage(messages.forgotPassword)}
                  </CapLink>
                </CapRow>
              )}

              <CapRow className="submit-section">
                <CapButton
                  type="primary"
                  size="large"
                  loading={isLoading || checkingSsoDomain}
                  disabled={
                    !formData.username ||
                    (showPasswordField && !formData.password) ||
                    checkingSsoDomain ||
                    (!showPasswordField && formData.username.trim() && !validateEmail(formData.username))
                  }
                  className="continue-button"
                  block
                  onClick={handleContinue}
                  tabIndex={3}
                >
                  {checkingSsoDomain
                    ? formatMessage(messages.checkingAuthentication)
                    : showPasswordField
                      ? formatMessage(messages.signInButton)
                      : formatMessage(messages.continueButton)
                  }
                </CapButton>
              </CapRow>

              {showPasswordField && (
                <CapRow className="back-link-section">
                  <span
                    className="back-link"
                    onClick={handleBackToEmail}
                    tabIndex={6}
                  >
                    {formatMessage(messages.back)}
                  </span>
                </CapRow>
              )}
            </CapRow>
          </CapRow>
          </div>
        </CapColumn>
      </CapRow>
    </CapRow>
  );
};


const mapStateToProps = createStructuredSelector({
  loginV2: makeSelectLoginV2(),
  otpSessionGuard: makeSelectOtpSessionGuard(),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      loginV2,
      ssoLoginV2,
      checkSsoDomain,
      clearError,
      resetPasswordField,
      checkOtpSession,
      logoutV2,
    },
    dispatch,
  ),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
  withRouter,
)(injectIntl(withStyles(NewLogin, styles)));
